import { type FC, useState } from 'react'
import { type AccountSchedulingSettings, EventTypeAccess, type SchedulingEventType } from '../../../../../models/Scheduling'
import { EventTypeImpactDialog } from '../../../../Scheduling/shared/EventTypeImpactDialog'
import { EventTypeNotLiveDialog } from '../../../../Scheduling/shared/EventTypeNotLiveDialog'
import { isOnAccountPage } from '../../../../Scheduling/shared/eventTypeHostState'
import { SettingsToggle } from '../../sections/SettingsSection'

const ACCOUNT_PAGE_LOCKED_HINT = 'Turn on the account booking page in Setup to use this.'

interface BookingPlacementCardProps {
  draft: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  disabled: boolean
  onDraftChange: (patch: Partial<SchedulingEventType>) => void
  onContinueToHosts: () => void
}

export const BookingPlacementCard: FC<BookingPlacementCardProps> = ({ draft, accountSettings, disabled, onDraftChange, onContinueToHosts }) => {
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false)
  const [isNotLiveOpen, setIsNotLiveOpen] = useState(false)
  const accountBookingEnabled = Boolean(accountSettings?.publicBookingPage.accountBookingEnabled)
  const showsAccountPage = draft.access === EventTypeAccess.ACCOUNT

  const handleIndividualPagesChange = (checked: boolean) => {
    if (checked) {
      onDraftChange({ active: true })
    } else {
      setIsDeactivateOpen(true)
    }
  }

  const handleDeactivateConfirm = () => {
    setIsDeactivateOpen(false)
    onDraftChange({ active: false })
  }

  const handleAccountPageChange = (checked: boolean) => {
    if (checked) {
      setIsNotLiveOpen(true)
    } else {
      onDraftChange({ accountPageVisible: false })
    }
  }

  const handleNotLiveContinue = () => {
    setIsNotLiveOpen(false)
    onDraftChange({ accountPageVisible: true })
    onContinueToHosts()
  }

  return (
    <div className='d-flex flex-column zocks-gap-0.5'>
      <span className='lead-small zocks-text-gray-500 text-uppercase'>Where clients can book this</span>
      <div className='d-flex flex-column gap-3 p-3 border rounded-3 zocks-border-color-gray-300 zocks-bg-color-white'>
        {showsAccountPage ? (
          <SettingsToggle
            label='Account booking page'
            description='Bookings go to the hosts you assign.'
            checked={Boolean(draft.accountPageVisible)}
            readOnly={disabled}
            disabled={!accountBookingEnabled}
            disabledHint={ACCOUNT_PAGE_LOCKED_HINT}
            ariaLabel='Account booking page'
            onChange={handleAccountPageChange}
          />
        ) : null}
        <SettingsToggle
          label='Individual booking pages'
          description='Each person can choose whether to use this event type.'
          checked={draft.active}
          readOnly={disabled}
          ariaLabel='Individual booking pages'
          onChange={handleIndividualPagesChange}
        />
      </div>
      {isDeactivateOpen ? (
        <EventTypeImpactDialog
          variant='deactivate'
          eventType={draft}
          staysOnAccountPage={showsAccountPage && isOnAccountPage(draft, accountBookingEnabled)}
          onConfirm={handleDeactivateConfirm}
          onCancel={() => setIsDeactivateOpen(false)}
        />
      ) : null}
      {isNotLiveOpen ? <EventTypeNotLiveDialog onContinue={handleNotLiveContinue} onCancel={() => setIsNotLiveOpen(false)} /> : null}
    </div>
  )
}
